import React, { FC } from 'react';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import { uniqBy } from 'lodash';
import StarsSvg from '@src/svgs/blue-stars.svg';
import { Container } from '@src/components/Container';
import { Avatar } from '@src/components/Avatar';
import { getNameInitials } from '@src/util/getNameInitials';
import { relativeTime } from '@src/util/relativeTime';
import { Tooltip } from '@src/components/Tooltip';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { ColouredTag } from '@src/components/ColouredTag';
import { JobStartTimeEnum } from '@src/graphql/generated';
import { USER_VERIFICATION_JOB_START_TIME } from '@src/constants';
import { ColouredTagColor } from '@src/components/ColouredTag/props';
import { Actions } from './Actions';
import { FeaturedTalentCardProps } from './props';
import './styles.scss';

export * from './Skeleton';
export * from './Placeholder';

const getImAvailableInColour = function (canStartIn: JobStartTimeEnum): ColouredTagColor {
  switch (canStartIn) {
    case JobStartTimeEnum.IMMEDIATELY:
    case JobStartTimeEnum.FEW_WEEKS:
      return ColouredTagColor.DARK_GREEN;
    case JobStartTimeEnum.ONE_MONTH:
      return ColouredTagColor.GREEN;
    case JobStartTimeEnum.FEW_MONTHS:
    case JobStartTimeEnum.HALF_YEAR:
      return ColouredTagColor.YELLOW;
    default:
      return ColouredTagColor.GRAY;
  }
};

export const FeaturedTalentCard: FC<FeaturedTalentCardProps> = ({
  wrapperClassName,
  userData,
  invitedUserIds,
  isDirectConnect,
  isSelected,
  isSaveActionVisible,
  openingId,
  onConfirmTalent,
  onFollowUp,
  onCardClick,
  onSelect,
  onArchive,
  onHide,
}) => {
  const avatarUrl = userData.user.avatar?.fullUrl;
  const nameInitials = getNameInitials(userData.user.name);
  const avatarColor = userData.user.avatarColor;
  const position = userData.user.position;
  const canStartIn = userData.userVerification.canStartIn;

  const originalSalaryValue = userData.user.profile.originalSalary.value || undefined;
  const originalSalaryCurrency = userData.user.profile.originalSalary.currency.symbol || undefined;
  const expectedSalary =
    originalSalaryValue && originalSalaryCurrency ? `${originalSalaryValue} ${originalSalaryCurrency}` : undefined;

  const locationNames = [
    userData.user.profile.location.shortName,
    ...(userData.user.profile.isRemote ? ['Remote'] : []),
    ...(userData.user.profile.isRelocation ? ['Relocation'] : []),
  ];

  const positionExpectations = userData.userVerification.positionExpectations;

  const timestamp = userData.userVerification.publishedAt;

  const uniqueExperience = uniqBy(userData.user.profile.workHistory, 'company.id');
  const workHistory = uniqueExperience.filter(({ company }) => !!company).slice(0, 3);

  return (
    <Container
      className={classnames('featured-talent-card', wrapperClassName, isSelected && 'featured-talent-card--selected')}
      onClick={onCardClick}
    >
      <div className="featured-talent-card__highlight-tag">
        <Svg src={StarsSvg} /> Open for offers
      </div>
      <div className="featured-talent-card__time">{relativeTime(timestamp, false)}</div>
      <div className="featured-talent-card__content">
        <div className="featured-talent-card__top-section">
          <Avatar
            avatarColor={avatarColor || undefined}
            initials={nameInitials}
            size={AvatarSize.XXX_LARGE}
            source={avatarUrl}
          />

          <div className="featured-talent-card__talent-main-info">
            <h4>{position}</h4>
            <p>
              {locationNames.join(', ')} • {expectedSalary}
            </p>
          </div>

          {canStartIn && (
            <ColouredTag
              className="featured-talent-card__available-in"
              colour={getImAvailableInColour(canStartIn)}
              text={USER_VERIFICATION_JOB_START_TIME[canStartIn].displayValue}
            />
          )}
        </div>

        <div className="featured-talent-card__bottom-section">
          <div>
            {positionExpectations ? (
              <div className="featured-talent-card__talent-looking-for">
                <h4>I’m looking for</h4>
                <p
                  className={classnames(
                    'featured-talent-card__expectations',
                    !!position && position?.length < 21 && 'featured-talent-card__expectations--two-rows',
                    !workHistory.length && 'featured-talent-card__expectations--expended',
                  )}
                >
                  {positionExpectations}
                  &nbsp; <span className="featured-talent-card__read-more">read&nbsp;more&nbsp;›</span>
                </p>
              </div>
            ) : null}

            {workHistory.length ? (
              <div className="featured-talent-card__talent-prev-experience">
                <h4>Previous experience</h4>
                <div className="featured-talent-card__companies">
                  {workHistory.map(({ company }) => (
                    <Tooltip
                      key={company?.id}
                      overlay={<>{company?.profile.name}</>}
                      overlayStyle={{
                        maxWidth: '25rem',
                        textAlign: 'center',
                      }}
                      placement="top"
                    >
                      <div className="featured-talent-card__company-logo-wrapper">
                        <Avatar
                          className="featured-talent-card__company-logo"
                          key={company?.id}
                          size={AvatarSize.MEDIUM}
                          source={company?.profile.logo?.fullUrl}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="featured-talent-card__actions">
        <Actions
          invitedUserIds={invitedUserIds}
          isDirectConnect={isDirectConnect}
          isSaveActionVisible={isSaveActionVisible}
          isSelected={isSelected}
          onConfirmTalent={onConfirmTalent}
          onFollowUp={onFollowUp}
          onHide={onHide}
          onArchive={onArchive}
          onSelect={onSelect}
          openingId={openingId}
          userData={userData}
        />
      </div>
    </Container>
  );
};
